import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { useNavigate } from "react-router"
import { isSettingsPagePath } from "../paths"

interface UnsavedChanges {
  setDirty: (key: string, dirty: boolean) => void
  /** Runs `leave` now, or once the user agrees to drop their edits. */
  confirmLeave: (leave: () => void) => void
}

const UnsavedChangesContext = createContext<UnsavedChanges | null>(null)

/**
 * Asks before unsaved edits are thrown away: on a link to another page (the
 * settings nav, the app sidebar, "Change in …"), on Back and the page picker
 * (through `useConfirmLeave`), and on reloading or closing the tab. The tabs
 * of one page keep their edits, so moving between them never asks.
 */
export function UnsavedChangesProvider({
  pagePath,
  children,
}: {
  /** The current page; links to it and below it (its tabs) stay on it. */
  pagePath: string
  children: ReactNode
}) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const [dirtyKeys, setDirtyKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null)
  const unsaved = dirtyKeys.size > 0

  const setDirty = useCallback((key: string, dirty: boolean) => {
    setDirtyKeys((current) => {
      if (current.has(key) === dirty) return current
      const next = new Set(current)
      if (dirty) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])

  const confirmLeave = useCallback(
    (leave: () => void) => {
      if (unsaved) setPendingLeave(() => leave)
      else leave()
    },
    [unsaved],
  )

  useEffect(() => {
    if (!unsaved) return

    function onClick(event: MouseEvent) {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return
      const link =
        event.target instanceof Element ? event.target.closest("a[href]") : null
      if (
        !(link instanceof HTMLAnchorElement) ||
        (link.target && link.target !== "_self") ||
        link.hasAttribute("download")
      )
        return
      const url = new URL(link.href)
      if (url.origin !== window.location.origin) return
      if (url.pathname === pagePath || url.pathname.startsWith(`${pagePath}/`))
        return
      // Capture phase, so this runs before the router's own click handler.
      event.preventDefault()
      event.stopPropagation()
      // Resumed the way the link would have gone: a move to another settings
      // page replaces the entry, so Back still leaves settings in one step.
      setPendingLeave(
        () => () =>
          navigate(`${url.pathname}${url.search}${url.hash}`, {
            replace: isSettingsPagePath(url.pathname),
          }),
      )
    }
    function onBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault()
      // Safari still asks for the legacy return value.
      event.returnValue = ""
    }

    document.addEventListener("click", onClick, true)
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => {
      document.removeEventListener("click", onClick, true)
      window.removeEventListener("beforeunload", onBeforeUnload)
    }
  }, [unsaved, pagePath, navigate])

  const value = useMemo(
    () => ({ setDirty, confirmLeave }),
    [setDirty, confirmLeave],
  )

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <AlertDialog
        open={pendingLeave !== null}
        onOpenChange={(open) => {
          if (!open) setPendingLeave(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate("SETTINGS/UNSAVED/TITLE")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate("SETTINGS/UNSAVED/DESCRIPTION")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {translate("SETTINGS/UNSAVED/STAY")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const leave = pendingLeave
                setPendingLeave(null)
                leave?.()
              }}
            >
              {translate("SETTINGS/UNSAVED/LEAVE")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </UnsavedChangesContext.Provider>
  )
}

/** Marks this form's edits as unsaved while `dirty`. A no-op outside the
 *  settings app, e.g. in the inquiry composer's signature panel. */
export function useUnsavedChanges(dirty: boolean) {
  const setDirty = useContext(UnsavedChangesContext)?.setDirty
  const key = useId()
  useEffect(() => {
    if (!setDirty || !dirty) return
    setDirty(key, true)
    return () => setDirty(key, false)
  }, [setDirty, key, dirty])
}

/** Leaves the page through the unsaved-changes check. */
export function useConfirmLeave(): (leave: () => void) => void {
  const confirmLeave = useContext(UnsavedChangesContext)?.confirmLeave
  return confirmLeave ?? ((leave) => leave())
}
